/**
 * Approval Hooks
 *
 * Provides hooks for approval operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from './queryKeys';

// Types matching the API response
export interface ApprovalListItem {
  workOrderNumber?: string | null;
  id: string;
  approvalType: string;
  status: string;
  urgency: string | null;
  description: string | null;
  customerDescription: string | null;
  totalCostCents: number;
  partsCostCents: number | null;
  laborCostCents: number | null;
  estimatedHours: number | null;
  determinedPath: string | null;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  mechanicId: string | null;
  mechanicName: string | null;
  vehicleRegNumber: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  bookingId: string | null;
  isEscalated: boolean;
  escalatedAt: string | null;
  expiresAt: string | null;
  reminderCount: number;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface ApprovalFilters {
  status?: string;
  type?: string;
  mechanicId?: string;
  customerId?: string;
  isEscalated?: boolean;
  q?: string;
  sortBy?: 'createdAt' | 'urgency' | 'totalCost';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

export interface ApprovalStats {
  totalCount: number;
  pendingCount: number;
  pendingValueCents: number;
  expiringToday: number;
  byType: { type: string; count: number }[];
  /** Totals per display status for the list chips (#2853). Optional until every API is deployed. */
  byStatus?: { pending: number; approved: number; rejected: number; expired: number };
}

/**
 * Hook to fetch approval statistics
 */
export function useApprovalStats() {
  return useQuery({
    queryKey: queryKeys.approvals.stats(),
    queryFn: () => api.get<ApprovalStats>('/approvals/stats'),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch paginated list of approvals
 */
export function useApprovals(
  filters: ApprovalFilters = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.approvals.list(filters as Record<string, unknown>),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<ApprovalListItem>>('/approvals', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 15 * 1000,
    enabled: options?.enabled,
  });
}

/**
 * Hook to fetch a single approval by ID
 */
export function useApproval(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.approvals.detail(id ?? ''),
    queryFn: () => api.get(`/approvals/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/** What the approve / decline routes return. */
export interface ApprovalDecisionResponse {
  success: boolean;
  approval: {
    id: string;
    status: string;
    finalDecision: string | null;
    approvedAmountCents?: number | null;
    /**
     * dual_manager: this was the first of two approvals by different managers;
     * the request stays open until a second manager approves (#3026).
     */
    awaitingSecondApproval?: boolean;
  };
}

/** `approvals` namespace keys for the refusal codes the decision routes return in `code` (#3026). */
const APPROVAL_REFUSAL_KEYS: Readonly<Record<string, string>> = {
  customer_declined: 'refusal.customerDeclined',
  customer_consent_required: 'refusal.customerConsentRequired',
  second_manager_required: 'refusal.secondManagerRequired',
};

/** The translation key for a refused approval decision, or null when the error is not a known refusal. */
export function approvalRefusalKey(error: unknown): string | null {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== 'object') return null;
  const code = (error.data as { code?: unknown }).code;
  return typeof code === 'string' ? (APPROVAL_REFUSAL_KEYS[code] ?? null) : null;
}

/**
 * Hook to approve a request
 */
export function useApproveApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      notes,
    }: {
      id: string;
      notes?: string;
    }) => api.post<ApprovalDecisionResponse>(`/approvals/${id}/approve`, { notes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.approvals.all });
      // Approval affects work order item status and booking status
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    },
  });
}

/**
 * Hook to decline a request
 */
export function useDeclineApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      reason,
    }: {
      id: string;
      reason?: string;
    }) => api.post(`/approvals/${id}/decline`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.approvals.all });
      // Decline affects work order item status and booking status
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    },
  });
}
